import {
    CircularProgress,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Typography,
    Stack,
} from '@mui/material';
import {
    ArrowForward,
    LocationOn as LocationOnIcon,
    Map as MapIcon,
    SentimentDissatisfied as SentimentDissatisfiedIcon
} from '@mui/icons-material';
import React, { useState, Dispatch, SetStateAction, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';
import TopNav from "./TopNav";
import Loader from "./Loader";
import Fonts from './Fonts';
import ScrollIndicator from './ScrollIndicator';
import { styled } from '@mui/material/styles';
import { theme, themeColors } from '../styles/theme';
import debounce from 'lodash/debounce';
import { useLoadScript } from '@react-google-maps/api';
import {
    trackNearMeSearch,
    trackPubNotFound,
    trackHomePageExit
} from '../utils/analytics';
import { Footer } from "./Footer";
import { checkPubSupport } from '../utils/checkPubSupport';
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle';
import WeatherTicker from "./WeatherTicker";
import Homescreen3DPub from './Homescreen3DPub';
import { useLocation } from '../contexts/LocationContext';
import howDoesItWorkImage from '../img/how_does_it_work.png';
import pintManIllustration from '../img/pint_man_illustration.png';
import { isCrawler } from '../utils/isCrawler';

// Define libraries array outside component to prevent recreation on each render
const googleMapsLibraries = ['places'] as any;

// A good featured pub with a known garden
const FEATURED_PUB_ID = 'ChIJic92JFoCdkgR1vWxmEfA_6M'; // Wickham

const StyledButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '12px',
    padding: '12px 20px',
    width: '100%',
    fontSize: '1rem',
}));

export const PrimaryButton = styled(StyledButton)(({ theme }) => ({
    background: 'transparent',
    color: '#000',
    fontSize: '1.1rem',
    fontFamily: "ApfelGrotezkRegular",
    borderRadius: '48px',
    transition: 'all 0.2s ease',
    '&:hover': {
        background: themeColors.lightYellow,
    },
    padding: '4px',
    backgroundColor: themeColors.lager,
}));

interface HomeProps {
    setTitle: Dispatch<SetStateAction<string>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    setLatitude: Dispatch<SetStateAction<number>>;
    setLongitude: Dispatch<SetStateAction<number>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

const Home = ({
    setTitle,
    search,
    setSearch,
    setLatitude,
    setLongitude,
    setShowBurgerMenuIcon,
    loading,
    setLoading
}: HomeProps) => {
    const {
        userLocation,
        hasLocationPermission,
        requestUserLocation,
        clearCustomSearchLocation,
    } = useLocation();

    const navigate = useNavigate();
    const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);
    const [featuredPubFailed, setFeaturedPubFailed] = useState<boolean>(false); // State for the fallback

    // Detect if this is a bot to skip 3D content
    const isBot = useMemo(() => {
        // Debug parameter to force bot mode
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('debug') === 'bot') {
            return true;
        }
        return isCrawler();
    }, []);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [autocompletePubsLoading, setAutocompletePubsLoading] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string | React.ReactNode | null>(null);
    const [pubNotFoundModalOpen, setPubNotFoundModalOpen] = useState<boolean>(false);
    const [notFoundPubName, setNotFoundPubName] = useState<string>('');
    const [flatMeshModalOpen, setFlatMeshModalOpen] = useState<boolean>(false);
    const [flatMeshErrorMessage, setFlatMeshErrorMessage] = useState<string>('');
    const [flatMeshDebugInfo, setFlatMeshDebugInfo] = useState<string>('');
    const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: googleMapsLibraries,
    });
    const placesServiceRef = useRef<any | null>(null);
    const autocompleteServiceRef = useRef<any | null>(null);

    useEffect(() => {
        setTitle('Suntap');
        setShowBurgerMenuIcon(true)
        window.scrollTo(0, 0);
        // Reset the failed state to allow retry (but keep component mounted)
        setFeaturedPubFailed(false);
    }, // eslint-disable-next-line
    []);

    // Initialize Google services
    useEffect(() => {
        if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
            try {
                const dummyElement = document.createElement('div');
                placesServiceRef.current = new window.google.maps.places.PlacesService(dummyElement);
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            } catch (error) {
                setSearchError('Failed to initialise search service');
            }
        }
    }, [isLoaded]);

    const navigateToPubNearMe = () => {
        clearCustomSearchLocation(); // Clear any custom search mode
        trackHomePageExit('pubs_near_me');
        navigate("/pubs");
    }

    const getLocationAndNavigate = async () => {
        setLoading(true);

        const location = await requestUserLocation(); // Don't force refresh - use caching

        if (location) {
            trackNearMeSearch(location.latitude, location.longitude);
            trackHomePageExit('near_me_with_location');
            navigateToPubNearMe();
        } else {
            setLoading(false);
            // Error is already in context, could show it here if needed
        }
    };

    // Handle search query change with debounce
    const debouncedPlacesSearch = useCallback(
        debounce((query: string) => {
            if (!query || query.length < 3 || !isLoaded || !autocompleteServiceRef.current) {
                setSearchResults([]);
                setAutocompletePubsLoading(false);
                return;
            }

            const request: any = {
                input: query,
                types: ['establishment'],
                componentRestrictions: { country: 'gb' }  // Restrict to UK
            };

            // Add location bias if we have user coordinates
            if (hasLocationPermission && userLocation) {
                // Create location bias with a reasonable radius (5000 meters = 5km)
                request.locationBias = {
                    center: { lat: userLocation.latitude, lng: userLocation.longitude },
                    radius: 5000
                };
            }

            autocompleteServiceRef.current.getPlacePredictions(
                request,
                (predictions: any[], status: any) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                        setSearchResults([]);
                        setAutocompletePubsLoading(false);
                        return;
                    }

                    // Filter to keep only establishment types that are likely pubs
                    const filteredPredictions = predictions.filter(prediction => {
                        const types = prediction.types || [];
                        return types.some((type: string) =>
                            ['bar', 'restaurant', 'establishment', 'food', 'point_of_interest'].includes(type)
                        );
                    });

                    setSearchResults(filteredPredictions);
                    setAutocompletePubsLoading(false);
                }
            );
        }, 300),
        [isLoaded, hasLocationPermission, userLocation]
    );

    // Update search results when query changes
    useEffect(() => {
        if (searchQuery.trim().length >= 3) {
            setAutocompletePubsLoading(true);
            debouncedPlacesSearch(searchQuery);
        } else {
            setSearchResults([]);
        }
    }, [searchQuery, debouncedPlacesSearch]);

    // Handle pub selection from PubSearchGoogle component
    const handlePubSelect = async (selectedPub: GooglePubResult) => {
        try {
            setLoading(true);
            setSearchError(null);

            // All the complex logic is now in one place.
            // The second argument is the callback for progress updates.
            const pubToView = await checkPubSupport(
                selectedPub,
                setLoadingMessage
            );
            
            // If the promise resolves, we know the pub is valid and ready for navigation.
            trackHomePageExit(`pub_${pubToView.google_place_id}`);
            navigate(`/pub/${pubToView.google_place_id}`, { state: { pubToView } });

        } catch (error: any) {
            // If the promise rejects, the error message is already user-friendly.
            console.error('Error processing pub:', error);
            trackPubNotFound(selectedPub.name || "pub");
            setSearchError(error.message); // The message is ready for the UI
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <Helmet>
                <title>Suntap | Find sunny pub gardens near you</title>
                <meta name="description" content="Instantly discover which pub gardens in London, Bristol and across the UK are in full sunshine right now." />
                <link rel="canonical" href="https://sun-tap.com/" />
                <meta name="keywords" content="suntap, sun tap, sunny pubs, beer gardens, pub gardens, london pubs, uk pubs, sunshine tracker" />
                
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Suntap" />
                <meta property="og:description" content="Find the sunniest beer gardens near you." />
                <meta property="og:url" content="https://sun-tap.com/" />
                <meta property="og:image" content="https://sun-tap.com/images/og-home.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Suntap" />
                <meta name="twitter:description" content="Find the sunniest beer gardens near you." />
                <meta name="twitter:image" content="https://sun-tap.com/images/og-home.jpg" />
                <script type="application/ld+json">
                    {JSON.stringify({ "@context": "https://schema.org", "@type": "WebSite", "url": "https://sun-tap.com/", "name": "Suntap", "potentialAction": { "@type": "SearchAction", "target": "https://sun-tap.com/search-area?q={search_term_string}", "query-input": "required name=search_term_string" } })}
                </script>
            </Helmet>
            <TopNav title="Suntap" />
            {!loading && (
                <Box sx={{ mt: '4vh' }}>
                    <WeatherTicker />
                </Box>
            )}

            <Box sx={{ pt: '3vh', pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingRight: '5%', paddingLeft: '5%' }}>
                <Fonts />

                {!loading && (
                    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
                        {loadError && <Typography color="error" sx={{ mb: 2 }}>Error loading search service: {loadError.message}</Typography>}
                        {searchError && <Typography color="error" sx={{ mb: 2 }}>{searchError}</Typography>}
                        {!isLoaded ? ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress sx={{ color: themeColors.orange }} /></Box>
                        ) : (
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                <PubSearchGoogle
                                    onPubSelect={handlePubSelect}
                                    placeholder="Search for a pub"
                                    initialQuery={searchQuery}
                                    onError={setSearchError}
                                    containerSx={{ width: '100%' }}
                                    attributionBottom="28px" // Higher position to account for mb: 3
                                    attributionRightEmpty="50px" // Closer to the right for Home page
                                    attributionRightWithText="70px" // Closer to the right when clear button is present
                                    inputSx={{
                                        mb: 3,
                                        borderRadius: '40px',
                                        fontFamily: "ApfelGrotezkRegular",
                                        backgroundColor: theme.palette.background.paper,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '.MuiOutlinedInput-input': {
                                            fontFamily: "ApfelGrotezkRegular",
                                            color: themeColors.darkBackground,
                                        },
                                        width: '100%',
                                        minWidth: '300px',
                                        display: 'flex',
                                    }}
                                />
                            </Stack>
                        )}
                        {/* Add loading indicator here */}
                        {loading && loadingMessage && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mt: 2,
                                p: 2,
                                background: 'rgba(0,0,0,0.05)',
                                borderRadius: 3,
                                border: '1px solid rgba(0,0,0,0.1)'
                            }}>
                                <CircularProgress size={24} sx={{ color: themeColors.orange, mb: 1 }} />
                                <Typography sx={{
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    color: themeColors.darkBackground,
                                    fontFamily: "ApfelGrotezkRegular",
                                    fontWeight: 500
                                }}>
                                    {loadingMessage}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {loading ? ( <Loader /> ) : (
                    <Box sx={{ width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800 }}>
                        
                        {featuredPubFailed || isBot ? (
                            // FALLBACK: Render the original static card (for errors or bots)
                            <Paper
                                elevation={2}
                                sx={{ p: 3, borderRadius: '24px', background: themeColors.lightYellow, backdropFilter: 'blur(16px)'}}
                                onClick={() => {
                                    if (hasLocationPermission) {
                                        getLocationAndNavigate();
                                    } else {
                                        setLocationModalOpen(true);
                                    }
                                }}
                            >
                                <Typography component="h1" sx={{ mb: 3, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                    Find sunny pubs near you
                                </Typography>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <PrimaryButton
                                        sx={{
                                            padding: '4px',
                                            width: '100%',
                                            minWidth: '180px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            px: '24px'
                                        }}
                                        endIcon={<LocationOnIcon />}
                                    >
                                        Find a sunny pub
                                    </PrimaryButton>
                                </Stack>
                            </Paper>
                        ) : (
                            // PRIMARY: Render the 3D component
                            <Paper
                                elevation={2}
                                sx={{ p: 0, borderRadius: '24px', background: '#a0c0f0', overflow: 'hidden', height: '350px', position: 'relative', cursor: 'pointer' }}
                            >
                                <Homescreen3DPub
                                    googlePlaceId={FEATURED_PUB_ID}
                                    onError={() => setFeaturedPubFailed(true)}
                                    onClick={() => {
                                        if (hasLocationPermission) {
                                            getLocationAndNavigate();
                                        } else {
                                            setLocationModalOpen(true);
                                        }
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.5) 100%)',
                                        pointerEvents: 'none' // Disable pointer events on the overlay
                                    }}
                                >
                                    <Typography component="h1" sx={{ fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2', color: 'white', textShadow: '2px 2px 6px rgba(0,0,0,0.7)'}}>
                                        Find sunny pubs near you
                                    </Typography>
                                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <PrimaryButton
                                            sx={{
                                                padding: '4px',
                                                width: '100%',
                                                minWidth: '180px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                px: '24px',
                                                pointerEvents: 'auto' // Enable pointer events for the button
                                            }}
                                            onClick={() => {
                                                if (hasLocationPermission) {
                                                    getLocationAndNavigate();
                                                } else {
                                                    setLocationModalOpen(true);
                                                }
                                            }}
                                            endIcon={<LocationOnIcon />}
                                        >
                                            Find a sunny pub
                                        </PrimaryButton>
                                    </Stack>
                                </Box>
                            </Paper>
                        )}

                        <Paper
                            elevation={2}
                            sx={{ p: 3, borderRadius: '24px', background: themeColors.lightYellow, backdropFilter: 'blur(16px)' }}
                            onClick={() => { trackHomePageExit('search_area'); navigate('/search-area'); }}
                        >
                            <Typography sx={{ mb: 10, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                Search an area and add your favourite pubs
                            </Typography>
                                <PrimaryButton
                                    sx={{
                                        padding: '4px',
                                        width: '100%',
                                        minWidth: '180px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: '24px'
                                }}
                                    endIcon={<MapIcon />}
                                >
                                    Explore the map
                                </PrimaryButton>
                        </Paper>

                        <Paper
                            elevation={2}
                            sx={{
                                position: 'relative',
                                p: 3,
                                borderRadius: '24px',
                                background: themeColors.lightYellow,
                                backdropFilter: 'blur(16px)',
                                height: '440px',
                            }}
                        >
                            <Typography sx={{ mb: 1, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                About us
                            </Typography>
                            <Typography sx={{ fontFamily: "ApfelGrotezkRegular", fontSize: '20px', fontWeight: '400', lineHeight: '1.2' }}>
                                Join our community of pub enthusiasts and help us build the most accurate guide to the UK's sunniest pub gardens.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '240px',
                                    position: 'absolute',
                                    bottom: '10px',
                                    right: '10px'
                                }}
                            >
                                <img
                                    src={pintManIllustration}
                                    alt="Man holding a pint"
                                    height="100%"
                                />
                            </Box>
                            <PrimaryButton
                                sx={{
                                    padding: '4px',
                                    width: '60%',
                                    minWidth: '180px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    px: '24px',
                                    position: 'absolute',
                                    bottom: '20px'
                                }}
                                onClick={() => { trackHomePageExit('about'); navigate("/about"); }}
                                endIcon={<ArrowForward />}
                            >
                                Find out more
                            </PrimaryButton>
                        </Paper>

                        {/*<Paper sx={{ p: 2, borderRadius: '24px', padding: '24px', maxHeight: '440px' }}>*/}
                        {/*    <Box*/}
                        {/*        sx={{*/}
                        {/*            display: 'flex',*/}
                        {/*            alignItems: 'center',*/}
                        {/*            justifyContent: 'center',*/}
                        {/*            height: '100%',*/}
                        {/*            mb: 2,*/}
                        {/*        }}*/}
                        {/*    >*/}
                        {/*        <img*/}
                        {/*            src={howDoesItWorkImage}*/}
                        {/*            alt="Phone with suntap on screen"*/}
                        {/*            width="100%"*/}
                        {/*        />*/}
                        {/*    </Box>*/}
                        {/*    <Typography sx={{ color: '#000', mb: 2, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>*/}
                        {/*        How does it work?*/}
                        {/*    </Typography>*/}
                        {/*    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>*/}
                        {/*        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>*/}
                        {/*            <PrimaryButton*/}
                        {/*                sx={{*/}
                        {/*                    padding: '4px',*/}
                        {/*                    width: '100%',*/}
                        {/*                    minWidth: '180px',*/}
                        {/*                    display: 'flex',*/}
                        {/*                    justifyContent: 'space-between',*/}
                        {/*                    alignItems: 'center',*/}
                        {/*                    px: '24px'*/}
                        {/*            }}*/}
                        {/*                onClick={() => { trackHomePageExit('about'); navigate("/about"); }}*/}
                        {/*                endIcon={<ArrowForward />}*/}
                        {/*            >*/}
                        {/*                Find out here*/}
                        {/*            </PrimaryButton>*/}
                        {/*        </Stack>*/}
                        {/*    </Box>*/}
                        {/*</Paper>*/}
                    </Box>
                )}

                {/* Static SEO content for search engines */}
                {isBot && !loading && (
                    <Box sx={{ p: 3, mt: 4 }}>
                        <Typography variant="h2" component="h2" sx={{ fontSize: '1.8rem', mb: 2 }}>
                            Find Sunny Pub Gardens Near Me
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Suntap is the UK's premier sunny pub garden finder. We help you discover pubs in the sun 
                            using real-time 3D sun tracking technology. Whether you're looking for sunny beer gardens 
                            in London, Bristol, Brighton or anywhere across the UK or even internationally, we show you exactly which pub 
                            gardens are bathed in sunshine right now, and even the best places and times to visit. All of our data is sourced from the community, so if your favourite pub isn't covered, add it to help us build the most accurate guide to the UK's sunniest pub gardens.
                        </Typography>
                        
                        <Typography variant="h3" component="h3" sx={{ fontSize: '1.4rem', mt: 3, mb: 2 }}>
                            Popular Searches for Sunny Pubs
                        </Typography>
                        <ul>
                            <li>Sunny pub gardens near me</li>
                            <li>Pubs in the sun today</li>
                            <li>Best beer gardens with sunshine</li>
                            <li>Outdoor pubs with sun all day</li>
                            <li>Sunniest pub gardens in London</li>
                        </ul>
                        
                        <Typography variant="body1" paragraph sx={{ mt: 3 }}>
                            Our innovative sun tracking shows you the best times to visit each pub garden for 
                            maximum sunshine. No more arriving at a pub only to find the garden is in shade!
                        </Typography>
                    </Box>
                )}

                <Footer isLoading={loading} />

                <Dialog
                    open={locationModalOpen}
                    onClose={() => setLocationModalOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, maxWidth: 500, px: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', pb: 1, pt: 3 }}>
                        Location Permission
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: 'text.secondary' }}>
                            We need your location to find sunny pubs near you. Your location data is only used to calculate distances and will not be stored.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%" alignItems="center">
                            <PrimaryButton
                                onClick={() => setLocationModalOpen(false)}
                                sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' } }}
                            >
                                Cancel
                            </PrimaryButton>
                            <PrimaryButton
                                onClick={() => { setLocationModalOpen(false); getLocationAndNavigate(); }}
                                sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' }, '&:hover': { backgroundColor: themeColors.mainYellow, } }}
                            >
                                Allow
                            </PrimaryButton>
                        </Stack>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={pubNotFoundModalOpen}
                    onClose={() => setPubNotFoundModalOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, maxWidth: 500, backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(16px)', px: 1 } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#1976d2', padding: '16px 24px' }}>
                        <SentimentDissatisfiedIcon sx={{ color: '#1976d2' }} /> Pub Addition Failed
                    </DialogTitle>
                    <DialogContent sx={{ padding: '24px' }}>
                        <DialogContentText sx={{ color: themeColors.darkBackground, mb: 2 }}>
                            We tried to automatically add <strong>"{notFoundPubName}"</strong> to our database but encountered an error.
                        </DialogContentText>
                        <Box sx={{ p: 2, backgroundColor: 'rgba(60, 104, 169, 0.05)', borderRadius: 2, border: '1px solid rgba(60, 104, 169, 0.1)', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: themeColors.darkBackground }}>
                                Please try searching for a different pub, or try again later.
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center' }}>
                        <PrimaryButton
                            onClick={() => setPubNotFoundModalOpen(false)}
                            sx={{ fontWeight: 500, padding: '4px', width: '40%', minWidth: '180px' }}
                        >
                            OK
                        </PrimaryButton>
                    </DialogActions>
                </Dialog>
                
                {!loading && (
                    <ScrollIndicator
                        iconColor={themeColors.orange}
                        backgroundColor="rgba(255, 255, 255, 0.2)"
                        backgroundOpacity={0.9}
                        size={48}
                        bottom={20}
                        right={20}
                        hideOnlyAtBottom={true}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Home;